/**
 * The room Kimmo wakes up in: two beds, the man still in the other one, the
 * yöpöydät and what is on them, the cupboards along the north wall, and the
 * one who never made it into his bed.
 *
 * Mostly dressing - but it is looked at from a metre away on the READY screen,
 * so it is furnished rather than blocked out. The beds themselves are hashed.
 */

import {
  cylinder,
  decor,
  solid,
  type CylinderSpec,
  type DecorSpec,
  type SleeperSpec,
  type SolidSpec,
} from '../world/Level';

/* ------------------------------------------------ pirtin makuuhuone ----- */

/**
 * Einon pirtti sleeps three: a bed against each side wall - Kimmo's on the
 * west, Juppe's on the east - the spawn on the floor between them, and a third
 * squeezed under the name board on the north wall. That one is `KOLMAS_SANKY`,
 * further down, with the man who never made it into it on the floor beside it.
 *
 * Juppe stays asleep. He has no lines and no bearing on the run - he is there
 * because you do not wake up alone in a rented pirtti the morning after, and
 * because a room with somebody still in it reads as a room somebody left in a
 * hurry.
 */
export const BED = {
  width: 1.2,
  length: 2.4,
  /** The frame. This is the collider; the mattress on top of it is decor. */
  frameHeight: 0.55,
  mattress: 0.12,
  /** Head end against the south wall, so both beds face the same way. */
  headZ: -5.4,
} as const;

const BED_FOOT_Z = BED.headZ + BED.length;
/** Top of the mattress: the height anything lying on a bed sits at. */
export const MATTRESS_TOP = BED.frameHeight + BED.mattress;

/** Flush with the walls: a half-metre slot beside a bed is only a place to get stuck. */
export const KIMMO_BED_X = 0.0;
export const ROOMMATE_BED_X = 10.0 - BED.width;

export function bedFrame(name: string, minX: number): SolidSpec {
  return solid(name, 'furniture', minX, BED.headZ, minX + BED.width, BED_FOOT_Z, BED.frameHeight);
}

/** Mattress and pillow. Visual only - the frame underneath is what you bump into. */
export function bedding(suffix: string, minX: number): DecorSpec[] {
  const top = BED.frameHeight;
  return [
    decor(
      `patja-${suffix}`,
      'fabric',
      minX + 0.08,
      BED.headZ + 0.08,
      minX + BED.width - 0.08,
      BED_FOOT_Z - 0.08,
      BED.mattress,
      top,
    ),
    decor(
      `tyyny-${suffix}`,
      'fabric',
      minX + 0.2,
      BED.headZ + 0.1,
      minX + BED.width - 0.2,
      BED.headZ + 0.68,
      0.14,
      top + BED.mattress,
    ),
  ];
}

/**
 * The oksennusampari, on the floor at the head end of Kimmo's bed - within
 * reach of the pillow, which is the entire reason a bucket is ever put there.
 *
 * Derived from the bed rather than typed out, so it follows if the bed moves.
 * Every part of it is decor: a knee-high bucket six centimetres off the bed
 * rail is exactly the thing to snag on in the first second of a run, and a
 * collider here would cost every stored leaderboard time to add a prop.
 */
export const AMPARI = {
  x: KIMMO_BED_X + BED.width + 0.22,
  z: BED.headZ - 0.22,
  /** Tapered, the way a stacking bucket is: narrow at the base, wide at the rim. */
  base: 0.115,
  rim: 0.16,
  height: 0.3,
} as const;

/* -------------------------------------------------- yöpöydät ----------- */

/**
 * A yöpöytä at the head end of each bed.
 *
 * `depth` is not a table's own measurement: it is the whole of the gap between
 * the head of a bed and the south wall behind it, so the two tables fill the
 * room's two south corners rather than leaving a slot in front of each of them.
 * That is the same rule the beds are placed by - a half-metre slot beside a bed
 * is only a place to get stuck - and `Level.test.ts` is what holds the numbers
 * together, because the wall is authored three hundred lines further down and a
 * derivation would have to reach forward to it. The top is level with the
 * mattress, which is the height a bedside table has in a room where somebody
 * has to reach it lying down.
 *
 * These are colliders, unlike the ampari a metre away, and that is not an
 * inconsistency. The bucket is decor because it stands in the open beside the
 * bed, six centimetres off the rail and square in the first second of a run;
 * these fill the two corners *behind* the pillows, which is the one part of
 * this room no line through it goes near. The rest of the room's furniture is
 * solid and so are they - which does mean they are in the fingerprint.
 */
export const YOPOYTA = {
  width: 0.5,
  /** The gap between the head of a bed and the wall behind it, exactly. */
  depth: 0.6,
  /** Level with the mattress beside it. */
  height: MATTRESS_TOP,
} as const;

export const YOPOYTA_Z = { back: BED.headZ - YOPOYTA.depth, front: BED.headZ } as const;

/** Flush with its own side wall, exactly like the bed it belongs to. */
export const KIMMON_YOPOYTA_X = KIMMO_BED_X;
export const NUKKUJAN_YOPOYTA_X = ROOMMATE_BED_X + BED.width - YOPOYTA.width;

export function yopoyta(suffix: string, minX: number): SolidSpec {
  return solid(
    `yopoyta-${suffix}`,
    'furniture',
    minX,
    YOPOYTA_Z.back,
    minX + YOPOYTA.width,
    YOPOYTA_Z.front,
    YOPOYTA.height,
  );
}

/**
 * Neljä mestaruuspokaalia, on Kimmo's own yöpöytä.
 *
 * The one thing in this room that says the man lying face-down in it is any
 * good at what he is about to be late for - and the joke is that all four of
 * them are behind his pillow, where he cannot see them and neither can anybody
 * who does not turn round on the READY screen. Which competition they are for
 * is on the plaque hung over them; see `PLAKAATTI` and `signs`.
 *
 * A cup is a plinth, a stem and a bowl, and the bowl is the only part that has
 * to be a cylinder: `topRadius` wider than `radius` is what makes it flare,
 * the same trick the ampari uses upside down. They stand in the order he won
 * them, each one a little taller than the year before, which is how a row like
 * this is put out by somebody who meant it to be looked at.
 *
 * What sets the size is that there are four of them on half a metre of table:
 * the rims have to clear each other, or the row reads as one lumpy brass object
 * rather than as four separate championships. That leaves two ways to make a
 * cup bigger, and the row uses the cheaper one first - it stands `margin` from
 * each end of the table rather than at even fifths of it, which buys a
 * centimetre and a half of pitch for nothing. The rest goes into height, which
 * is the dimension a trophy is read by anyway: the shortest of the four now
 * stands as tall as the tallest one used to.
 *
 * All decor, for the reason everything else within arm's reach of the spawn is:
 * the tables under them are the last colliders this room needs.
 */
const POKAALI = {
  /** The plinth, which is the only square thing about a cup. */
  base: 0.084,
  baseHeight: 0.026,
  stemRadius: 0.015,
  /** The bowl: narrow where it meets the stem, wide at the rim. */
  cupFoot: 0.028,
  cupRim: 0.046,
  cupHeight: 0.08,
  /** Stem lengths, in the order they stand: oldest at the wall, newest out. */
  stems: [0.048, 0.066, 0.084, 0.105],
  /** How far onto the table the row stands, measured from the wall. */
  rowZ: 0.23,
  /**
   * And how close to each end of it the outermost cups stand. Wide enough that
   * a rim still clears the table edge and the wall behind it, narrow enough
   * that the row uses the table it is on. `Level.test.ts` holds both ends.
   */
  margin: 0.07,
} as const;

/** Top of the tallest bowl: what the plaque hung over the row has to clear. */
export const POKAALI_TOP =
  MATTRESS_TOP + POKAALI.baseHeight + Math.max(...POKAALI.stems) + POKAALI.cupHeight;

/**
 * The plaque over the cups, which is the half of the joke that names it.
 *
 * A row of brass on a bedside table is only a row of brass until something
 * says what it was won at, and "lanijatkojen mestaruus" is funnier engraved
 * than any cup can be on its own. Two lines rather than one strip: `createSign`
 * sizes the font off the sign's height and never shrinks to fit, so twenty-two
 * characters on one line would be a metre of wall for a plaque hung over a
 * fifty-centimetre table.
 */
export const PLAKAATTI = { width: 0.34, height: 0.16 } as const;

export function pokaalit(): { decor: DecorSpec[]; cylinders: CylinderSpec[] } {
  const top = MATTRESS_TOP;
  const z = YOPOYTA_Z.back + POKAALI.rowZ;
  const items: DecorSpec[] = [];
  const cups: CylinderSpec[] = [];

  POKAALI.stems.forEach((stem, index) => {
    const n = index + 1;
    // Spread from end to end of the table rather than at even fifths of it:
    // the outermost two stand `margin` in, and the rest divide what is left.
    // That is the whole of what pays for the size of them - see `POKAALI`.
    const x =
      KIMMON_YOPOYTA_X +
      POKAALI.margin +
      ((YOPOYTA.width - 2 * POKAALI.margin) * index) / (POKAALI.stems.length - 1);
    const half = POKAALI.base / 2;
    items.push(
      decor(
        `pokaali-${n}-jalusta`,
        'wood',
        x - half,
        z - half,
        x + half,
        z + half,
        POKAALI.baseHeight,
        top,
      ),
    );
    const stemY = top + POKAALI.baseHeight;
    cups.push(
      cylinder(`pokaali-${n}-varsi`, 'brass', x, z, POKAALI.stemRadius, stem, stemY, {
        radialSegments: 8,
      }),
      cylinder(`pokaali-${n}-malja`, 'brass', x, z, POKAALI.cupFoot, POKAALI.cupHeight, stemY + stem, {
        radialSegments: 12,
        topRadius: POKAALI.cupRim,
      }),
    );
  });

  return { decor: items, cylinders: cups };
}

export const POKAALIT = pokaalit();

/**
 * The lappu on the other yöpöytä, stood on the table against the wall.
 *
 * A sign rather than a prop with a sign on it: the paper *is* the quad, the
 * same way every other piece of writing in the level is. It faces +z, i.e. into
 * the room, because a note left for somebody is left facing the way they will
 * be looking - and it is the only thing on this level written in lower case,
 * because it is handwriting rather than signage.
 */
export const LAPPU = { width: 0.28, height: 0.19 } as const;

/* -------------------------------------------- yöpöydän tavarat --------- */

/**
 * What the two of them took to bed, which is most of what this room says
 * about the two of them.
 *
 * Juppe has a vesipullo on his table and Kimmo has a tuoppi on his. That is
 * the whole of it: one of them drank water before he went down, the other one
 * carried a beer up from the navetta, and the oksennusampari on the floor at
 * Kimmo's pillow is the rest of the sentence. The room is already built as two
 * of one thing rather than as two different things - see `NIMILAPPU` - so both
 * stand in the same spot on their own table, and mirrored like that it is the
 * contents you read rather than the arrangement.
 *
 * The front strip is also the only part of either table that is free: the
 * pokaalit have the back of Kimmo's and the lappu is stood against the back of
 * Juppe's. It is the part you can reach lying down, which is where something
 * you were still drinking ends up anyway.
 *
 * Decor, like everything else in this room within arm's reach of the spawn.
 * The tables under them are the last colliders it needs.
 *
 * How far in they stand is set by the tighter of the two tables, which is
 * Juppe's: the lappu behind it is `LAPPU.width` centred on half a metre, so
 * anything further out than about 77 mm is a bottle stood in front of a note
 * somebody is meant to read. `Level.test.ts` holds that clearance, because the
 * lappu is authored four thousand lines further down among the signs.
 */
export const YOPOYDAN_INSET = 0.07;

/** The end of a yöpöytä nearest the room, and the front edge of it. */
export const KIMMON_TUOPPI = {
  x: KIMMON_YOPOYTA_X + YOPOYTA.width - YOPOYDAN_INSET,
  z: YOPOYTA_Z.front - YOPOYDAN_INSET,
  /**
   * Nearly full, because that is the funnier end of it: he did not sit up
   * drinking it, he got two mouthfuls in and went down. A finished glass says
   * he meant to have it - see `tuoppi()` for what `fill` draws.
   */
  fill: 0.7,
} as const;

export const JUPEN_VESIPULLO = {
  x: NUKKUJAN_YOPOYTA_X + YOPOYDAN_INSET,
  z: YOPOYTA_Z.front - YOPOYDAN_INSET,
} as const;

/**
 * A half-litre kaupan vesipullo, drawn the way `tuoppi()` draws a glass: as a
 * stack rather than as a container, because nothing in the blockout is
 * transparent. Water up to the level he drank it down to, and the empty part
 * of the bottle above it.
 *
 * The shoulder is the only part of it that is not a plain drum, and it is the
 * part that makes the silhouette a bottle rather than a tin can: `topRadius`
 * takes it from the body's radius down to the neck's in one taper, which is
 * the pokaali bowls' trick upside down. The cap is `metal` rather than the
 * bottle's own `glass` so the top of it reads as a line rather than as a fade.
 */
const VESIPULLO = {
  radius: 0.033,
  /** The straight-sided part: everything below the shoulder. */
  bodyHeight: 0.13,
  shoulderHeight: 0.045,
  neckRadius: 0.014,
  neckHeight: 0.028,
  capRadius: 0.017,
  capHeight: 0.016,
  /** How much of it is left by morning. */
  fill: 0.45,
} as const;

export function vesipullo(name: string, x: number, z: number, y: number): CylinderSpec[] {
  const water = VESIPULLO.bodyHeight * VESIPULLO.fill;
  const shoulderY = y + VESIPULLO.bodyHeight;
  const neckY = shoulderY + VESIPULLO.shoulderHeight;
  const capY = neckY + VESIPULLO.neckHeight;
  return [
    cylinder(`${name}-vesi`, 'water', x, z, VESIPULLO.radius, water, y, { radialSegments: 10 }),
    cylinder(
      `${name}-runko`,
      'glass',
      x,
      z,
      VESIPULLO.radius,
      VESIPULLO.bodyHeight - water,
      y + water,
      { radialSegments: 10 },
    ),
    cylinder(`${name}-olka`, 'glass', x, z, VESIPULLO.radius, VESIPULLO.shoulderHeight, shoulderY, {
      radialSegments: 10,
      topRadius: VESIPULLO.neckRadius,
    }),
    cylinder(`${name}-kaula`, 'glass', x, z, VESIPULLO.neckRadius, VESIPULLO.neckHeight, neckY, {
      radialSegments: 10,
    }),
    cylinder(`${name}-korkki`, 'metal', x, z, VESIPULLO.capRadius, VESIPULLO.capHeight, capY, {
      radialSegments: 10,
    }),
  ];
}

const ROOMMATE_BODY = { length: 1.9, width: 0.85, height: 0.4 } as const;

/**
 * In the quotes the venue writes koodinimet in, the same way the player is
 * `Koodinimi "Kimmo"` on the HUD.
 *
 * It lives on the lappu over his bed and nowhere else. `SleeperSpec.name` is in
 * the simulation fingerprint, so renaming the collider under him would retire
 * every leaderboard time to relabel a piece of scenery.
 *
 * Its opposite number is `PLAYER_ALIAS`, over Kimmo's bed on the far wall. Both
 * lappu are the same piece of tape - see `NIMILAPPU`.
 */
export const ROOMMATE_NAME = '"Juppe"';

/**
 * The lappu with a koodinimi on it, taped to the wall over the head of a bed.
 *
 * There is one over Kimmo's bed and one over Juppe's, because a pirtti rented
 * by the mattress is a pirtti where whose bed is whose has to be written down
 * somewhere. They are the same size, at the same height, the same distance off
 * their own wall and the same distance down the bed - so the room reads as two
 * of one thing rather than as a sign and a different sign, and moving one
 * moves both.
 *
 * THE THIRD BED HAS NONE, and that is the point of it. There was a lappu on
 * that wall and taking it off is what makes the bed worth looking at: a
 * labelled empty bed is a man who is late, and an unlabelled one with a body
 * face down in front of it is a man nobody in this room can name. The gag is
 * better as a question than as an answer, and the room only has the one place
 * an answer could have been written down.
 *
 * `offWall` is a strip of tape's worth. Flush with the wall it would fight the
 * plaster for the same pixels; a hand's width out it would be a shelf.
 */
export const NIMILAPPU = {
  width: 1.1,
  height: 0.3,
  /** Head height for somebody standing beside the bed, not lying in it. */
  y: 1.55,
  offWall: 0.04,
  /** How far down the bed from its head end the lappu is taped. */
  alongBed: 0.5,
} as const;

/**
 * Against the wall rather than centred on the mattress: that leaves no
 * player-width slot between him and the wall for anybody who hops onto the bed
 * to wedge themselves into.
 */
export const ROOMMATE: SleeperSpec = {
  name: 'pirtin-nukkuja',
  center: [10.0 - ROOMMATE_BODY.width / 2, BED.headZ + BED.length / 2],
  orientation: 'z',
  ...ROOMMATE_BODY,
  y: BED.frameHeight + BED.mattress,
};

/**
 * Kimmo himself, asleep in his own bed, drawn only while the run has not
 * started and taken away the moment WASD wakes him.
 *
 * Deliberately *not* in `sleepers`: those are colliders, and a collider that
 * comes and goes between the READY screen and the first step is exactly the
 * kind of thing the fingerprint exists to refuse. This one is a picture, and
 * it never reaches the simulation - see `LevelDefinition.sleepingPlayer`.
 *
 * Centred on the mattress rather than shoved against the wall like Juppe, for
 * the same reason turned around: with nobody able to walk into him, the only
 * thing left to get right is that he looks like a man in a bed, head on the
 * pillow.
 */
export const SLEEPING_PLAYER: SleeperSpec = {
  name: 'kimmo-unessa',
  center: [KIMMO_BED_X + BED.width / 2, BED.headZ + BED.length / 2],
  orientation: 'z',
  // The same body Juppe has. Two lads, one pirtti, one size of mattress.
  ...ROOMMATE_BODY,
  y: MATTRESS_TOP,
};
